import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, TrendingUp, Building2, Crown, Zap, Target, Swords, Trophy, Users, Heart, Home, MapPin, Coins, Gem, Calendar, Gamepad2, Flame, Search, Lock } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';

const ICON_MAP = {
  'star': Star, 'trending-up': TrendingUp, 'building': Building2, 'crown': Crown,
  'zap': Zap, 'target': Target, 'sword': Swords, 'trophy': Trophy,
  'users': Users, 'heart': Heart, 'home': Home, 'map': MapPin,
  'coins': Coins, 'gem': Gem, 'calendar': Calendar, 'gamepad': Gamepad2,
  'flame': Flame, 'search': Search,
};

const COLOR_MAP = {
  'star': 'sunny-yellow', 'trending-up': 'mint-green', 'building': 'sky-blue', 'crown': 'sunny-yellow',
  'zap': 'coral-orange', 'target': 'mint-green', 'sword': 'coral-orange', 'trophy': 'sunny-yellow',
  'users': 'lavender', 'heart': 'coral-orange', 'home': 'mint-green', 'map': 'sky-blue',
  'coins': 'sunny-yellow', 'gem': 'lavender', 'calendar': 'coral-orange', 'gamepad': 'lavender',
  'flame': 'coral-orange', 'search': 'mint-green',
};

const AchievementsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newlyEarned, setNewlyEarned] = useState([]);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/achievements/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setNewlyEarned(res.data.achievements.filter(a => a.new).map(a => a.id));
    } catch (e) {
      console.error('Failed to fetch achievements:', e);
    } finally {
      setLoading(false);
    }
  };

  const earned = data?.achievements?.filter(a => a.earned) || [];
  const locked = data?.achievements?.filter(a => !a.earned) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-sunny-yellow/10 to-white">
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard" className="w-10 h-10 bg-white border-2 border-soft-gray/30 rounded-xl flex items-center justify-center hover:bg-soft-gray/20 transition-colors" data-testid="achievements-back">
            <ArrowLeft className="w-5 h-5 text-text-medium" />
          </Link>
          <div className="flex-1">
            <h1 className="font-display text-2xl text-text-dark">Badges</h1>
            <p className="text-text-medium text-sm">
              {data ? `${data.total_earned} / ${data.total_available} earned` : 'Loading...'}
            </p>
          </div>
          {data && (
            <div className="bg-sunny-yellow/20 rounded-xl px-4 py-2 border border-sunny-yellow/30">
              <span className="font-display text-2xl text-sunny-yellow">{data.total_earned}</span>
              <span className="text-text-medium text-xs ml-1">/ {data.total_available}</span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {data && (
          <div className="bg-white border-2 border-soft-gray/20 rounded-2xl p-4 mb-6 shadow-sm">
            <div className="h-4 bg-soft-gray/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-sunny-yellow to-coral-orange rounded-full transition-all duration-1000"
                style={{ width: `${(data.total_earned / data.total_available) * 100}%` }}
              />
            </div>
            <p className="text-center text-text-medium text-xs mt-2">
              {Math.round((data.total_earned / data.total_available) * 100)}% complete
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-text-medium">Loading badges...</div>
        ) : (
          <>
            {/* Earned badges */}
            {earned.length > 0 && (
              <div className="mb-8">
                <h2 className="font-display text-lg text-text-dark mb-3">Earned</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {earned.map(ach => {
                    const IconComp = ICON_MAP[ach.icon] || Star;
                    const color = COLOR_MAP[ach.icon] || 'sunny-yellow';
                    const isNew = newlyEarned.includes(ach.id);
                    return (
                      <div
                        key={ach.id}
                        className={`bg-white border-2 rounded-2xl p-4 text-center shadow-sm transition-all ${
                          isNew ? `border-${color} ring-2 ring-${color}/30 animate-pulse` : 'border-soft-gray/20'
                        }`}
                        data-testid={`badge-${ach.id}`}
                      >
                        <div className={`w-12 h-12 bg-${color}/20 rounded-full flex items-center justify-center mx-auto mb-2`}>
                          <IconComp className={`w-6 h-6 text-${color}`} />
                        </div>
                        <p className="font-display text-sm text-text-dark">{ach.name}</p>
                        <p className="text-text-medium text-xs mt-1">{ach.desc}</p>
                        {isNew && (
                          <span className={`inline-block mt-2 px-2 py-0.5 bg-${color}/20 text-${color} text-xs font-bold rounded-full`}>
                            NEW! +10 coins
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked badges */}
            {locked.length > 0 && (
              <div>
                <h2 className="font-display text-lg text-text-dark mb-3">Locked</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {locked.map(ach => (
                    <div
                      key={ach.id}
                      className="bg-soft-gray/10 border-2 border-dashed border-soft-gray/30 rounded-2xl p-4 text-center opacity-60"
                      data-testid={`badge-locked-${ach.id}`}
                    >
                      <div className="w-12 h-12 bg-soft-gray/20 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Lock className="w-6 h-6 text-text-medium/40" />
                      </div>
                      <p className="font-display text-sm text-text-medium">{ach.name}</p>
                      <p className="text-text-medium/70 text-xs mt-1">{ach.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AchievementsPage;
